/**
 * DecisionFlow — PlanLibrary
 * ui/src/components/admin/PlanLibrary.jsx
 * S611
 *
 * Certified compensation package library — Plan Library tab.
 * Replaces TemplateLibrary.jsx. TemplateLibrary.jsx preserved as reference — do not delete.
 *
 * Spec authority: comp_policies_spec_v2_2.md §3
 *
 * Certified library filter: is_platform_certified = true
 * NEVER filter on template_origin — orthogonal to certification (spec §2, permanent).
 *
 * Deploy flow:
 *   Per-row [ Deploy ] button → PlanLibraryIntentModal → POST /api/v1/template-library/deploy (direct)
 *   Checkbox bulk → [ Deploy Selected (N) ] → PlanLibraryIntentModal (N packages, per-package iteration) → POST per package
 *   DeploymentWizard is NOT used here — its sole mount point is TemplateLibrary.jsx (S627 fix)
 *
 * Seen-flag: React useState only. Not sessionStorage. Not localStorage. Resets on reload by design.
 * Checkbox: enabled only for NOT_DEPLOYED rows.
 * Mobile: checkbox column hidden, bulk deploy not available.
 *
 * Endpoints:
 *   GET  /api/v1/template-library/templates?certified_only=true&include_gtm=true&fiscal_period_id={id}
 *   POST /api/v1/template-library/deploy  (via PlanLibraryIntentModal)
 *
 * CSS: rgb(var(--color-*)) tokens only. Zero hardcoded Tailwind color classes. Zero dark: variants.
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, Filter, X, ChevronDown, ChevronRight,
  Sparkles, CheckSquare, Square, Minus, CheckCircle2,
} from "lucide-react";

import PlanLibraryPopup        from "./PlanLibraryPopup.jsx";
import PlanLibraryIntentModal  from "./PlanLibraryIntentModal.jsx";

const API_BASE = "/api/v1";

// ---------------------------------------------------------------------------
// Sales motion groups — spec §3: section grouping, Specialty collapsed by default
// ---------------------------------------------------------------------------
const MOTION_GROUPS = [
  { key: "new_logo",  label: "NEW LOGO / REVENUE GROWTH", defaultOpen: true  },
  { key: "expand",    label: "EXPAND & GROW",              defaultOpen: true  },
  { key: "retain",    label: "RETAIN & RENEW",             defaultOpen: true  },
  { key: "team",      label: "TEAM & COLLABORATIVE",       defaultOpen: true  },
  { key: "specialty", label: "SPECIALTY",                  defaultOpen: false,
    subtitle: "Generally relevant for specific industries — expand to browse" },
];

// ---------------------------------------------------------------------------
// Deployment status config
// ---------------------------------------------------------------------------
const STATUS_CONFIG = {
  NOT_DEPLOYED:      { label: "Not Deployed",       tone: "muted",   border: ""                                          },
  PENDING_APPROVAL:  { label: "Pending Approval",   tone: "warning", border: "border-l-2 border-l-[rgb(var(--color-warning))]" },
  COMPONENTS_PENDING:{ label: "Pending · Configure",tone: "warning", border: "border-l-2 border-l-[rgb(var(--color-warning))]" },
  ACTIVE:            { label: "Active",              tone: "success", border: "border-l-2 border-l-[rgb(var(--color-success))]" },
};

// ---------------------------------------------------------------------------
// Data hook
// ---------------------------------------------------------------------------
function useFetch(url) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    if (!url) { setLoading(false); return; }
    setLoading(true);
    fetch(url, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [url, refresh]);

  return { data, loading, error, refetch: () => setRefresh((n) => n + 1) };
}

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------
function LoadingSkeleton() {
  return (
    <div className="space-y-2 px-4 py-3">
      {[1,2,3,4,5].map((i) => (
        <div key={i} className="flex items-center gap-4">
          <div className="h-4 w-4 rounded skeleton" />
          <div className="h-4 flex-1 rounded skeleton" />
          <div className="h-4 w-24 rounded skeleton" />
          <div className="h-4 w-32 rounded skeleton" />
          <div className="h-4 w-16 rounded skeleton" />
          <div className="h-4 w-28 rounded skeleton" />
          <div className="h-6 w-20 rounded skeleton" />
        </div>
      ))}
    </div>
  );
}

function EmptyLibrary() {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <Sparkles className="mb-3 h-8 w-8 text-[rgb(var(--color-text-muted))]" />
      <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
        No packages available
      </p>
      <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
        No certified packages found for the current filters.
      </p>
    </div>
  );
}

function ModuleChips({ modules = [] }) {
  if (!modules.length) return null;
  const visible  = modules.slice(0, 3);
  const overflow = modules.length - 3;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {visible.map((m) => (
        <span
          key={m.module_code}
          className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium
                     bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]"
        >
          {m.module_name ?? m.module_code}
        </span>
      ))}
      {overflow > 0 && (
        <span className="text-[10px] text-[rgb(var(--color-text-muted))]">+{overflow}</span>
      )}
    </div>
  );
}

function MotionPill({ motion: salesMotion }) {
  const MOTION_LABELS = {
    new_logo:  "New Logo",
    expand:    "Expand",
    retain:    "Retain",
    team:      "Team",
    specialty: "Specialty",
  };
  return (
    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium
                     border border-[rgb(var(--color-border))] text-[rgb(var(--color-text-muted))]">
      {MOTION_LABELS[salesMotion] ?? salesMotion ?? "—"}
    </span>
  );
}

function TierPill({ tier }) {
  const isEnterprise = tier === "enterprise";
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium
                      ${isEnterprise
                        ? "bg-[rgb(var(--color-primary))]/10 text-[rgb(var(--color-primary))]"
                        : "bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]"}`}>
      {isEnterprise ? "Enterprise" : "Standard"}
    </span>
  );
}

function StatusCell({ template }) {
  const status = template.tenant_deployment_status ?? "NOT_DEPLOYED";
  const cfg    = STATUS_CONFIG[status] ?? STATUS_CONFIG.NOT_DEPLOYED;
  const count  = template.active_plan_ids?.length ?? 0;
  const reps   = template.total_assigned_count ?? 0;

  if (status === "ACTIVE" && count > 0) {
    return (
      <span className="text-tok-xs font-medium text-[rgb(var(--color-success))]">
        Active · {count} instance{count !== 1 ? "s" : ""} · {reps} rep{reps !== 1 ? "s" : ""}
      </span>
    );
  }
  if (status === "PENDING_APPROVAL") {
    return <span className="text-tok-xs font-medium text-[rgb(var(--color-warning))]">Pending Approval</span>;
  }
  if (status === "COMPONENTS_PENDING") {
    return <span className="text-tok-xs font-medium text-[rgb(var(--color-warning))]">Pending · Configure Components</span>;
  }
  if (template.is_coming_soon) {
    return <span className="text-tok-xs italic text-[rgb(var(--color-text-muted))]">Coming Soon</span>;
  }
  return <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">Not Deployed</span>;
}

// ---------------------------------------------------------------------------
// Filter panel
// ---------------------------------------------------------------------------
const SALES_MOTIONS  = ["new_logo","expand","retain","team","specialty"];
const TIER_OPTIONS   = ["standard","enterprise"];
const STATUS_OPTIONS = ["NOT_DEPLOYED","PENDING_APPROVAL","ACTIVE"];
const INDUSTRY_OPTIONS = ["SaaS","Technology","Professional Services","Manufacturing",
                          "Distribution","Financial Services","Pharmaceuticals"];

function FilterPanel({ filters, onChange, onClear, activeCount }) {
  function toggle(key, value) {
    const current = filters[key] ?? [];
    const next    = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    onChange({ ...filters, [key]: next });
  }

  function FilterGroup({ label, optKey, options, labelFn }) {
    return (
      <div>
        <p className="mb-1.5 text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">
          {label}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {options.map((opt) => {
            const active = (filters[optKey] ?? []).includes(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() => toggle(optKey, opt)}
                className={`rounded px-2 py-0.5 text-tok-xs font-medium transition-colors
                  ${active
                    ? "bg-[rgb(var(--color-primary))] text-white"
                    : "bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-border))]"}`}
              >
                {labelFn ? labelFn(opt) : opt}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  const motionLabels = { new_logo:"New Logo", expand:"Expand", retain:"Retain", team:"Team", specialty:"Specialty" };
  const statusLabels = { NOT_DEPLOYED:"Not Deployed", PENDING_APPROVAL:"Pending", ACTIVE:"Active" };

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.15 }}
      className="overflow-hidden"
    >
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 space-y-4">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <FilterGroup label="Sales Motion" optKey="motions"    options={SALES_MOTIONS}   labelFn={(o) => motionLabels[o]} />
          <FilterGroup label="Tier"         optKey="tiers"      options={TIER_OPTIONS}    labelFn={(o) => o === "enterprise" ? "Enterprise" : "Standard"} />
          <FilterGroup label="Status"       optKey="statuses"   options={STATUS_OPTIONS}  labelFn={(o) => statusLabels[o]} />
          <FilterGroup label="Industry"     optKey="industries" options={INDUSTRY_OPTIONS} />
        </div>
        <div className="flex items-center justify-between border-t border-[rgb(var(--color-border))] pt-3">
          <label className="flex items-center gap-2 text-tok-xs text-[rgb(var(--color-text-muted))] cursor-pointer">
            <input
              type="checkbox"
              checked={filters.showTenantVersions ?? false}
              onChange={(e) => onChange({ ...filters, showTenantVersions: e.target.checked })}
              className="rounded"
            />
            Show tenant versions
          </label>
          {activeCount > 0 && (
            <button
              type="button"
              onClick={onClear}
              className="text-tok-xs text-[rgb(var(--color-primary))] hover:underline"
            >
              Clear all
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Table row
// ---------------------------------------------------------------------------
function PackageRow({
  template, isSelected, isSelectable, onSelect,
  onRowClick, onDeploy, isMobile,
}) {
  const status    = template.tenant_deployment_status ?? "NOT_DEPLOYED";
  const cfg       = STATUS_CONFIG[status] ?? STATUS_CONFIG.NOT_DEPLOYED;
  const isComingSoon = template.is_coming_soon;

  return (
    <tr
      className={`group border-b border-[rgb(var(--color-border))] transition-colors
                  hover:bg-[rgb(var(--color-surface-muted))]/60 cursor-pointer
                  ${cfg.border ? cfg.border.replace("border-l-2","") : ""}`}
      style={cfg.border ? { borderLeftWidth: "2px", borderLeftStyle: "solid",
        borderLeftColor: status === "ACTIVE"
          ? "rgb(var(--color-success))"
          : status === "PENDING_APPROVAL" || status === "COMPONENTS_PENDING"
          ? "rgb(var(--color-warning))" : "transparent" } : {}}
      onClick={() => onRowClick(template)}
    >
      {/* Checkbox — hidden on mobile */}
      {!isMobile && (
        <td className="w-10 px-3 py-3" onClick={(e) => e.stopPropagation()}>
          {isSelectable ? (
            <button
              type="button"
              onClick={() => onSelect(template.template_id)}
              className="text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-primary))]"
              aria-label={isSelected ? "Deselect" : "Select"}
            >
              {isSelected
                ? <CheckSquare className="h-4 w-4 text-[rgb(var(--color-primary))]" />
                : <Square className="h-4 w-4" />}
            </button>
          ) : (
            <span className="block h-4 w-4" />
          )}
        </td>
      )}

      {/* Package Name */}
      <td className="px-3 py-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-tok-sm font-medium text-[rgb(var(--color-text))] group-hover:text-[rgb(var(--color-primary))]">
            {template.template_name}
          </span>
          {template.similar_to && (
            <span className="text-[10px] text-[rgb(var(--color-text-muted))]">
              Similar to: {template.similar_to}
            </span>
          )}
          {template.tenant_derived_from && (
            <span className="pl-2 border-l-2 border-[rgb(var(--color-border))] text-[10px] text-[rgb(var(--color-text-muted))]">
              Your version · Derived from certified package
            </span>
          )}
        </div>
      </td>

      {/* Sales Motion — hidden on mobile */}
      {!isMobile && (
        <td className="w-36 px-3 py-3">
          <MotionPill motion={template.sales_motion} />
        </td>
      )}

      {/* Plan Types — hidden on mobile */}
      {!isMobile && (
        <td className="w-52 px-3 py-3">
          <ModuleChips modules={template.modules ?? []} />
        </td>
      )}

      {/* Tier — hidden on mobile */}
      {!isMobile && (
        <td className="w-20 px-3 py-3">
          <TierPill tier={template.tier} />
        </td>
      )}

      {/* Status */}
      <td className="w-44 px-3 py-3">
        <StatusCell template={template} />
      </td>

      {/* Actions — status-aware per spec §3:
           NOT_DEPLOYED      → [ Deploy ] button
           PENDING_APPROVAL  → View → link (plan submitted, awaiting CA-2 approval)
           ACTIVE            → View → link (plan live, instances visible in popup)
           COMPONENTS_PENDING → Configure → link (plan deployed, components need config)
           is_coming_soon    → nothing
      */}
      <td className="w-28 px-3 py-3 text-right" onClick={(e) => e.stopPropagation()}>
        {isComingSoon ? null : status === "NOT_DEPLOYED" ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDeploy([template]); }}
            className="rounded border border-[rgb(var(--color-primary))] px-3 py-1
                       text-tok-xs font-medium text-[rgb(var(--color-primary))]
                       hover:bg-[rgb(var(--color-primary))]/10 transition-colors"
          >
            Deploy
          </button>
        ) : status === "COMPONENTS_PENDING" ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onRowClick(template); }}
            className="rounded border border-[rgb(var(--color-warning))]/50 px-3 py-1
                       text-tok-xs font-medium text-[rgb(var(--color-warning))]
                       hover:bg-[rgb(var(--color-warning))]/10 transition-colors"
          >
            Configure →
          </button>
        ) : (
          /* PENDING_APPROVAL | ACTIVE — view detail popup */
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onRowClick(template); }}
            className="rounded border border-[rgb(var(--color-border))] px-3 py-1
                       text-tok-xs font-medium text-[rgb(var(--color-text-muted))]
                       hover:border-[rgb(var(--color-primary))] hover:text-[rgb(var(--color-primary))]
                       transition-colors"
          >
            View →
          </button>
        )}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Section group
// ---------------------------------------------------------------------------
function MotionSection({
  group, templates, selectedIds, onSelect,
  onRowClick, onDeploy, isMobile, showHeader,
}) {
  const [open, setOpen] = useState(group.defaultOpen);

  if (!templates.length && group.defaultOpen) return null;
  if (!templates.length) return null;

  return (
    <tbody>
      {/* Section header row */}
      <tr
        className="cursor-pointer select-none border-b border-[rgb(var(--color-border))]
                   bg-[rgb(var(--color-surface-muted))]"
        onClick={() => setOpen((o) => !o)}
      >
        <td colSpan={isMobile ? 3 : 7} className="px-3 py-2">
          <div className="flex items-center gap-2">
            {open
              ? <ChevronDown className="h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
              : <ChevronRight className="h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />}
            <span className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
              {group.label}
            </span>
            <span className="ml-auto text-tok-xs text-[rgb(var(--color-text-muted))]">
              {templates.length} package{templates.length !== 1 ? "s" : ""}
            </span>
          </div>
          {!open && group.subtitle && (
            <p className="mt-0.5 pl-5 text-[10px] text-[rgb(var(--color-text-muted))]">
              {group.subtitle}
            </p>
          )}
        </td>
      </tr>

      {/* Package rows */}
      {open && templates.map((t) => (
        <PackageRow
          key={t.template_id}
          template={t}
          isSelected={selectedIds.has(t.template_id)}
          isSelectable={!t.is_coming_soon}
          onSelect={onSelect}
          onRowClick={onRowClick}
          onDeploy={onDeploy}
          isMobile={isMobile}
        />
      ))}
    </tbody>
  );
}

// ---------------------------------------------------------------------------
// Select-all checkbox header cell
// ---------------------------------------------------------------------------
function SelectAllCell({ templates, selectedIds, onSelectAll }) {
  const eligible  = templates.filter(
    (t) => (t.tenant_deployment_status ?? "NOT_DEPLOYED") === "NOT_DEPLOYED" && !t.is_coming_soon
  );
  const allSelected = eligible.length > 0 && eligible.every((t) => selectedIds.has(t.template_id));
  const someSelected = eligible.some((t) => selectedIds.has(t.template_id));

  return (
    <th className="w-10 px-3 py-2">
      <button
        type="button"
        onClick={() => onSelectAll(eligible, allSelected)}
        className="text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-primary))]"
        aria-label="Select all"
      >
        {allSelected
          ? <CheckSquare className="h-4 w-4 text-[rgb(var(--color-primary))]" />
          : someSelected
          ? <Minus className="h-4 w-4" />
          : <Square className="h-4 w-4" />}
      </button>
    </th>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function PlanLibrary({ period, isLocked, onConfigureComponents }) {
  const fiscalPeriodId = period?.fiscal_period_id ?? null;

  const url = fiscalPeriodId
    ? `${API_BASE}/template-library/templates?certified_only=true&include_gtm=true&fiscal_period_id=${fiscalPeriodId}`
    : `${API_BASE}/template-library/templates?certified_only=true&include_gtm=true`;

  const { data, loading, error, refetch } = useFetch(url);

  const allTemplates = data?.templates ?? [];

  // Fetch tenant roles, segments, and selling motions for deploy modal
  // GET /api/v1/roles    → { "data": [...] }  (confirmed roles_router.py)
  // GET /api/v1/segments → { "data": [...] }  (confirmed segments_router.py S630)
  // GET /api/v1/selling-motions → { "data": [...] }  (confirmed selling_motions_router.py)
  const { data: rolesData }    = useFetch(`${API_BASE}/roles`);
  const { data: segmentsData } = useFetch(`${API_BASE}/segments`);
  const { data: motionsData }  = useFetch(`${API_BASE}/selling-motions`);
  const tenantRoles          = rolesData?.data    ?? [];
  const tenantSegments       = segmentsData?.data ?? [];
  const tenantSellingMotions = motionsData?.data  ?? [];

  // UI state
  const [search,         setSearch]         = useState("");
  const [showFilters,    setShowFilters]     = useState(false);
  const [filters,        setFilters]         = useState({});
  const [selectedIds,    setSelectedIds]     = useState(new Set());
  const [seenPackages,   setSeenPackages]    = useState(new Set());
  const [popupTemplate,  setPopupTemplate]   = useState(null);
  const [deployQueue,    setDeployQueue]     = useState([]); // templates pending deploy flow
  const [deployError,    setDeployError]     = useState(null); // inline error surface
  const [deployedPlan,   setDeployedPlan]    = useState(null); // { planId, templateName, componentDefaults } — set after path=plan_with_components deploy
  const [isMobile,       setIsMobile]        = useState(window.innerWidth < 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // Active filter count
  const activeFilterCount = useMemo(() => {
    return (filters.motions?.length ?? 0)
      + (filters.tiers?.length ?? 0)
      + (filters.statuses?.length ?? 0)
      + (filters.industries?.length ?? 0)
      + (filters.showTenantVersions ? 1 : 0);
  }, [filters]);

  // Filtered templates
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allTemplates.filter((t) => {
      // Tenant versions
      if (!filters.showTenantVersions && t.tenant_derived_from) return false;
      // Search
      if (q && !(
        t.template_name.toLowerCase().includes(q) ||
        (t.sales_motion ?? "").toLowerCase().includes(q) ||
        (t.industry_tags ?? []).some((i) => i.toLowerCase().includes(q)) ||
        (t.modules ?? []).some((m) => (m.module_name ?? "").toLowerCase().includes(q))
      )) return false;
      // Motion filter
      if (filters.motions?.length > 0 && !filters.motions.includes(t.sales_motion)) return false;
      // Tier filter
      if (filters.tiers?.length > 0 && !filters.tiers.includes(t.tier)) return false;
      // Status filter
      if (filters.statuses?.length > 0 && !filters.statuses.includes(t.tenant_deployment_status ?? "NOT_DEPLOYED")) return false;
      // Industry filter
      if (filters.industries?.length > 0 && !filters.industries.some((i) => (t.industry_tags ?? []).includes(i))) return false;
      return true;
    });
  }, [allTemplates, search, filters]);

  // Group by sales motion
  const byMotion = useMemo(() => {
    const map = {};
    MOTION_GROUPS.forEach((g) => { map[g.key] = []; });
    filtered.forEach((t) => {
      const key = t.sales_motion ?? "specialty";
      if (!map[key]) map[key] = [];
      map[key].push(t);
    });
    return map;
  }, [filtered]);

  // Handlers
  const handleRowClick = useCallback((template) => {
    setSeenPackages((prev) => new Set([...prev, template.template_id]));
    setPopupTemplate(template);
  }, []);

  const handleSelect = useCallback((templateId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(templateId)) next.delete(templateId);
      else next.add(templateId);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback((eligible, allSelected) => {
    if (allSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        eligible.forEach((t) => next.delete(t.template_id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        eligible.forEach((t) => next.add(t.template_id));
        return next;
      });
    }
  }, []);

  // Deploy flow trigger
  const handleDeploy = useCallback((templates) => {
    setDeployQueue(templates);
  }, []);

  const handleBulkDeploy = useCallback(() => {
    const selected = filtered.filter((t) => selectedIds.has(t.template_id));
    if (selected.length) setDeployQueue(selected);
  }, [filtered, selectedIds]);

  // After deploy confirmed + intent chosen → POST directly to template-library/deploy.
  // Certified packages never open DeploymentWizard — that component's sole mount point
  // is TemplateLibrary.jsx (the predecessor, preserved as reference). S627.
  const handleIntentConfirm = useCallback(async (template, path, roleId, segmentId, sellingMotionIds, oteMin, oteMax, oteMidpoint, businessUnitCode, certificationStatus) => {
    setDeployError(null);
    setDeployQueue((q) => q.filter((t) => t.template_id !== template.template_id));

    try {
      const res = await fetch(`${API_BASE}/template-library/deploy`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          template_ids:       [template.template_id],
          fiscal_period_id:   period?.fiscal_period_id,
          path,
          role_id:            roleId            ?? null,
          segment_id:         segmentId         ?? null,
          selling_motion_ids: sellingMotionIds  ?? [],
          ote_min:            oteMin            ?? null,
          ote_max:            oteMax            ?? null,
          ote_midpoint:       oteMidpoint       ?? null,
          business_unit_code: businessUnitCode  ?? null,
          certification_status: certificationStatus ?? null,  // S739 Step 8 — GREEN|AMBER|RED from Review & Deploy overlay
        }),
      });

      // Refetch regardless — on 5xx the plan may have been written before the error.
      // Table status is the source of truth, not the response body.
      refetch();
      setSelectedIds(new Set());

      if (!res.ok) {
        const envelope = await res.json().catch(() => ({}));
        const msg = envelope?.error?.message ?? `Deploy failed (HTTP ${res.status}). Check the table — if status shows Pending Approval the deploy succeeded.`;
        setDeployError(msg);
      } else if (path === "plan_with_components") {
        // S680: capture plan_id from response to bridge CA to component configuration.
        // component_defaults is a top-level key on the template object — template.component_defaults.
        // template.gtm contains only prose fields (objective, behavioral_driver, etc.) — NOT defaults.
        // S681 fix: was incorrectly reading template.gtm?.component_defaults (always undefined).
        // Response shape: { deployed: [{ plan_id, template_code, path, component_count, ... }], ... }
        const body = await res.json().catch(() => ({}));
        const planId = body?.deployed?.[0]?.plan_id ?? null;
        if (planId) {
          setDeployedPlan({
            planId,
            templateName: template.template_name,
            componentDefaults: template.component_defaults ?? {},
          });
        }
      }
    } catch (e) {
      // Network-level failure — backend was not reached, safe to retry.
      refetch();
      setSelectedIds(new Set());
      setDeployError("Network error — could not reach the server. Check your connection and try again.");
    }
  }, [period, refetch]);

  const handleDeployCancel = useCallback(() => {
    setDeployQueue([]);
    setDeployError(null);
  }, []);

  const handlePopupDeploy = useCallback((template) => {
    setPopupTemplate(null);
    setDeployQueue([template]);
  }, []);

  const selectedBulkCount = selectedIds.size;

  return (
    <div className="space-y-3">

      {/* ── Header ── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-tok-lg font-semibold text-[rgb(var(--color-text))]">Plan Library</h2>
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            Browse and deploy data-fit Curated compensation packages for this fiscal period.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Bulk deploy button — appears when ≥1 selected */}
          <AnimatePresence>
            {selectedBulkCount > 0 && (
              <motion.button
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                type="button"
                onClick={handleBulkDeploy}
                className="rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-xs
                           font-semibold text-white hover:opacity-90 transition-opacity"
              >
                Deploy Selected ({selectedBulkCount})
              </motion.button>
            )}
          </AnimatePresence>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[rgb(var(--color-text-muted))]" />
            <input
              type="text"
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]
                         pl-8 pr-3 text-tok-xs text-[rgb(var(--color-text))] placeholder:text-[rgb(var(--color-text-muted))]
                         focus:outline-none focus:ring-1 focus:ring-[rgb(var(--color-primary))] w-48"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[rgb(var(--color-text-muted))]"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>

          {/* Filters toggle */}
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-tok-xs font-medium transition-colors
                        ${showFilters || activeFilterCount > 0
                          ? "border-[rgb(var(--color-primary))] bg-[rgb(var(--color-primary))]/10 text-[rgb(var(--color-primary))]"
                          : "border-[rgb(var(--color-border))] text-[rgb(var(--color-text-muted))] hover:border-[rgb(var(--color-primary))]"}`}
          >
            <Filter className="h-3.5 w-3.5" />
            Filters
            {activeFilterCount > 0 && (
              <span className="ml-0.5 rounded-full bg-[rgb(var(--color-primary))] px-1.5 py-0.5 text-[10px] text-white">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* ── Post-deploy component configuration callout ── */}
      {/* S680: shown after path=plan_with_components deploy. Bridges CA to PlanBuilder
          pre-populated with certified defaults. Dismissed with X or after navigating. */}
      <AnimatePresence>
        {deployedPlan && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="flex items-start justify-between gap-3 rounded-xl border border-[rgb(var(--color-success))]/30
                       bg-[rgb(var(--color-success))]/5 px-4 py-3"
          >
            <div className="flex items-start gap-3 min-w-0">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(var(--color-success))]" />
              <div className="min-w-0">
                <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
                  {deployedPlan.templateName} deployed as draft plan
                </p>
                <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))] leading-relaxed">
                  Components are pre-configured from certified defaults. Review and submit for
                  approval to make this plan assignment-eligible.
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {onConfigureComponents && (
                <button
                  type="button"
                  onClick={() => {
                    onConfigureComponents(deployedPlan);
                    setDeployedPlan(null);
                  }}
                  className="rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-xs
                             font-semibold text-white hover:opacity-90 transition-opacity whitespace-nowrap"
                >
                  Configure Components →
                </button>
              )}
              <button
                type="button"
                onClick={() => setDeployedPlan(null)}
                className="text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]"
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Filter panel ── */}
      <AnimatePresence>
        {showFilters && (
          <FilterPanel
            filters={filters}
            onChange={setFilters}
            onClear={() => setFilters({})}
            activeCount={activeFilterCount}
          />
        )}
      </AnimatePresence>

      {/* ── Table ── */}
      <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] overflow-hidden">
        {loading ? (
          <LoadingSkeleton />
        ) : error ? (
          <div className="px-6 py-8 text-center">
            <p className="text-tok-sm text-[rgb(var(--color-danger))]">Failed to load Plan Library.</p>
            <button
              type="button"
              onClick={refetch}
              className="mt-2 text-tok-xs text-[rgb(var(--color-primary))] hover:underline"
            >
              Retry
            </button>
          </div>
        ) : (
          <table className="w-full border-collapse">
            <thead className="border-b border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))]">
              <tr>
                {!isMobile && (
                  <SelectAllCell
                    templates={filtered}
                    selectedIds={selectedIds}
                    onSelectAll={handleSelectAll}
                  />
                )}
                <th className="px-3 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">
                  Package Name
                </th>
                {!isMobile && (
                  <th className="w-36 px-3 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">
                    Sales Motion
                  </th>
                )}
                {!isMobile && (
                  <th className="w-52 px-3 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">
                    Plan Types
                  </th>
                )}
                {!isMobile && (
                  <th className="w-20 px-3 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">
                    Tier
                  </th>
                )}
                <th className="w-44 px-3 py-2 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">
                  Status
                </th>
                <th className="w-28 px-3 py-2 text-right text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">
                  Actions
                </th>
              </tr>
            </thead>

            {MOTION_GROUPS.map((group) => (
              <MotionSection
                key={group.key}
                group={group}
                templates={byMotion[group.key] ?? []}
                selectedIds={selectedIds}
                onSelect={handleSelect}
                onRowClick={handleRowClick}
                onDeploy={handleDeploy}
                isMobile={isMobile}
              />
            ))}

            {filtered.length === 0 && (
              <tbody>
                <tr>
                  <td colSpan={isMobile ? 3 : 7}>
                    <EmptyLibrary />
                  </td>
                </tr>
              </tbody>
            )}
          </table>
        )}
      </div>

      {/* ── Know More Popup ── */}
      <AnimatePresence>
        {popupTemplate && (
          <PlanLibraryPopup
            template={popupTemplate}
            seen={seenPackages.has(popupTemplate.template_id)}
            period={period}
            onClose={() => setPopupTemplate(null)}
            onDeploy={handlePopupDeploy}
          />
        )}
      </AnimatePresence>

      {/* ── Deploy error banner ── */}
      {deployError && (
        <div className="flex items-start justify-between gap-3 rounded-lg border border-[rgb(var(--color-danger))]/30
                        bg-[rgb(var(--color-danger))]/5 px-4 py-3">
          <p className="text-tok-xs text-[rgb(var(--color-danger))] leading-relaxed">{deployError}</p>
          <button
            type="button"
            onClick={() => setDeployError(null)}
            className="shrink-0 text-[rgb(var(--color-danger))]/60 hover:text-[rgb(var(--color-danger))]"
            aria-label="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* ── Deploy Confirmation + Intent Modal ── */}
      <AnimatePresence>
        {deployQueue.length > 0 && (
          <PlanLibraryIntentModal
            templates={deployQueue}
            period={period}
            onConfirm={handleIntentConfirm}
            onCancel={handleDeployCancel}
            roles={tenantRoles}
            segments={tenantSegments}
            sellingMotions={tenantSellingMotions}
          />
        )}
      </AnimatePresence>

    </div>
  );
}
